/**
 * Javascript functions for Annotations page. Handles clicks on Sort controls, filter options, buttons to change the view
 * Style, and annotations management controls.
 */

import FetchHelpers from "../fetch-helpers";
import {appColors, copyToClipboard, darkenColor, lightenColor, stripPageNumbers} from "../utilities/general";
import {Spinner} from "../spinner";
import {AsyncNotice} from "../notices/notice";
import {addPaginationNavigationHandlers, setCollectionOptions} from "../pagination";
import {
  arrayColumn,
  createTagManagementControls,
  removeAllChildNodes,
  tagsSortingFunction,
  updateTagManagementElements,
} from "../annotations/manage-tags";
import {
  insertOverlayOnElement, removeOverlayOnElement
} from "../utilities/general";

/**
 * Set the options object that manages the state of annotation components (filters, view, sort, etc.).
 * if clearFilters is set, it sets the filters objects to the default values.
 *
 * @returns {any}
 */
function setOptionValues(clearFilters = false) {
  // initialize with current options
  const currentOptions = getCurrentOptions();
  let options = structuredClone(currentOptions);

  // ensure not empty object
  if(Object.keys(options).length === 0) {
    options = setOptionValues(true);
  }

  if (clearFilters) {
    options.filters = defaultFilters();
    options.filters.clear = true;

    return options;
  }

  // 0. event handler for filtered label 'x' button
  const filteredClearBtn = document.querySelector('#total i');
  if(filteredClearBtn) {
    filteredClearBtn.addEventListener('click', async (event) => {
      event.preventDefault();

      const spinnerOptions = {
        name: 'clear-filters',
        targetSelector: '#total',
        insertPosition: 'afterend',
        withWrapper: true
      };

      const  spinner = new Spinner(...Object.values(spinnerOptions));

      spinner.add();
      await clearFiltersHandler(event, 'all');
      spinner.remove();
    });
  }

  // 1. update collection style
  const viewStyleElem = document.querySelector('#view-style');

  // if the user is changing the collection style
  if (viewStyleElem) {
    const viewElem = viewStyleElem.querySelector('.icon-on');
    if(viewElem) {
      const { dataset: { value } } = viewElem;
     options.collection.style = value;
    }
  }

  // 2. update sort option
  const sortElem = document.querySelector('#sort-by');
  if (sortElem) {
    options.sort = sortElem.value;
    options.collection.sort = sortElem.value;
  }

  // 3. update color filter options
  const colorFilterElems = document.querySelectorAll('.filter-colors div input[type="checkbox"]');
  if (colorFilterElems && colorFilterElems.length > 0) {
    options.filters.colors = [];
    colorFilterElems.forEach((elem) => {
      if (clearFilters) {
        elem.checked = false;
      } else {
        if (elem.checked) {
          options.filters.colors.push(elem.id);
        }
      }
    });
  }

  // 4. update text filters
  let textSensitivity, textGrouping;
  const textFilterElem = document.querySelector('#filter-by-text');
  const textGroupingElem = document.querySelector("#word-grouping");
  const textSensitivityElem = document.querySelector("#case-sensitivity");
  if (textSensitivityElem) {
    textSensitivity = textSensitivityElem.checked ? 'sensitive' : 'insensitive'
  }

  if (clearFilters) {
    if(textFilterElem) {
      textFilterElem.value = '';
    }

    if(textSensitivityElem) {
      textSensitivityElem.checked = false; // default to 'insensitive'
    }

    if(textGroupingElem) {
      textGroupingElem.checked = true; // default to 'All'
    }
  } else {
    if (textSensitivity) {
      options.filters.text.sensitivity = textSensitivity;
    }

    if (textGroupingElem) {
      let {on, off} = textGroupingElem.dataset;
      textGrouping = textGroupingElem.checked ? on : off;
      options.filters.text.grouping = textGrouping;
    }

    if (textFilterElem) {
      options.filters.text.words = textFilterElem.value.trim();
    }
  }

  // 5. update tags filter
  if(clearFilters) {
    const tagGroupingElem = document.querySelector('#tag-grouping');
    tagGroupingElem.checked = true; // default to 'All'
  }

  options.filters.tags.ids = [];

  const selectedTags = document.querySelectorAll('.selected-tags .tag');
  if (selectedTags && selectedTags.length > 0) {
    options.filters.tags.ids = [];

    selectedTags.forEach((elem) => {
      if(!options.filters.tags.ids.includes(elem.dataset.id)) {
        options.filters.tags.ids.push(elem.dataset.id);
      }
    })
  }

  // tags grouping
  const tagGroupingElem = document.querySelector('#tag-grouping');
  if (tagGroupingElem) {
    const { on, off } = tagGroupingElem.dataset;
    options.filters.tags.grouping = tagGroupingElem.checked ? on : off;
  }

  // 6. update itemsPerPage
  const showElem = document.querySelector('.items-per-page');
  if (showElem) {
    options.collection.itemsPerPage = parseInt(showElem.value);
  }

  // 7. update sort by
  if (sortElem) {
    options.sort = sortElem.value;
    options.collection.sort = sortElem.value;
  }

  return options;
}

/**
 * Add the event handlers for the Annotations page.
 *
 * @returns {Promise<void>}
 */
async function addAnnotationPageEventHandlers() {
  const textFilterBtn = document.querySelector(".filter-text button");
  const sortElem = document.querySelector('#sort-by');
  const colorFilterElems = document.querySelectorAll(".filter-colors div input[type=\'checkbox\']");
  const textFilterElemSelector = '.filter-text input[type="text"]'; // used below for spinner options as well

  const textFilterElem = document.querySelector(textFilterElemSelector);
  const viewStyleElems = document.querySelectorAll('#view-style a');
  const spinnerSelector = 'div #clear-all-filters';

  // 1. Collection style click handler
  viewStyleElems.forEach((styleElement) => {
    styleElement.addEventListener('click', async (event) => {
      event.preventDefault();

      const { target } = event;
      const listItemAnchor = target.closest('a');
      const newCollectionStyle = listItemAnchor.dataset.value;

      // get current values
      let annotationsWrapper = document.querySelector('[data-paginated-collection]');
      let currentOptions = JSON.parse(annotationsWrapper.dataset.options);

      // if changing Collection style
      if (currentOptions.collection.style !== newCollectionStyle.style) {
        // 1. swap style element visibility
        let activeElem = document.querySelector('#view-style a.icon-on');
        let disabledElem = document.querySelector('#view-style a.icon-off');

        activeElem.classList.replace('icon-on', 'icon-off');
        disabledElem.classList.replace('icon-off', 'icon-on');

        // 2. set options
        let options = setOptionValues();

        options.spinner = {
          name: 'collection-style',
          targetSelector: '#view-style',
          insertPosition: 'afterbegin',
        };

        // 3. update collection
        let result = await updateCollection(options);
        emphasizeFilteredTagsOnAnnotations();
        manageFilterAccordions();

        // 4. manage list icons
        if (result.success) {
          activeElem.classList.replace('icon-on', 'icon-off');
          disabledElem.classList.replace('icon-off', 'icon-on');

          // update collection style on annotations wrapper
          let annotationsWrapper = document.querySelector('[data-paginated-collection]');
          annotationsWrapper.dataset.options = JSON.stringify(options);
        } else {
          // revert icons and dataset values
          activeElem.classList.replace('icon-off', 'icon-on');
          disabledElem.classList.replace('icon-on', 'icon-off');
        }
      }
    });
  });

  // 2. 'Sort By' selection handlers
  if (sortElem) {
    sortElem.addEventListener('change', async () => {
      disableFilters();

      let options = setOptionValues();
      options.spinner = {
        name: 'sort-by',
        targetSelector: '#annotations-sort-by',
        insertPosition: 'afterbegin'
      };

      setCollectionOptions(options, options.collection.style, options.paginatorName)
      await updateCollection(options);
      manageFilterAccordions();

      enableFilters();
    })
  }

  // 3. Color filters click handler
  if (colorFilterElems) {
    colorFilterElems.forEach((input) => {
      input.addEventListener('click', async () => {
        disableFilters();

        let options = setOptionValues();
        options.spinner = {
          name: 'color-filter',
          targetSelector: spinnerSelector,
          insertPosition: 'afterend',
        };
        await updateCollection(options);
        manageFilterAccordions();

        enableFilters();
      });
    });
  }

  // 4. text filter click handler
  if (textFilterElem) {
    // click handler on filter button
    textFilterBtn.addEventListener('click', async () => {
      disableFilters();

      let options = setOptionValues();
      options.spinner = {
        name: 'text-filter',
        targetSelector: spinnerSelector,
        insertPosition: 'afterbegin',
      };

      await updateCollection(options);
      manageFilterAccordions();

      enableFilters();
    })

    // 5. 'Enter' key handler on text filter input field
    textFilterElem.addEventListener('keypress', async (event) => {
      if (event.key === 'Enter') {
        disableFilters();

        let options = setOptionValues();
        options.spinner = {
          name: 'text-filter',
          targetSelector: spinnerSelector,
          insertPosition: 'afterbegin',
        };

        if (options.filters.text.words) {
          await updateCollection(options);
          manageFilterAccordions();
        }

        enableFilters();
      }
    })

    // 6. text filter case sensitivity
    textFilterSensitivityChangeHandler();

    // 7. Text filter grouping
    textFilterGroupingClickHandler();
  }

  // 8. add click handlers to tags in filter
  addHandlersToTagsInFilter();

  // 9. Clear filters button
  await addClearFiltersHandlers();

  // 10. Annotation action handlers
  await addAnnotationsActionHandlers();

  // 11. Tags collections full view
  addMarkTagsFullViewHandler();

  // 12. Tags filter input text handler
  addKeyUpTextInputOnTagsFilterHandler();

  // 13. filter toggle icon handlers
  tagToggleButtonsClickHandler();

  // 14. tag icon on edit options handler
  annotationOptionDivClickHandler();
}

/**
 * CLick event handler on annotation options div tag icon
 */
function annotationOptionDivClickHandler() {
  const annotationsCollection = document.querySelectorAll('#annotations-collection .annotation-options');
  if(annotationsCollection) {
    annotationsCollection.forEach((item) => {
      const tagIcon = item.querySelector('[data-name="manage-tags"]');
      if(tagIcon) {
        tagIcon.addEventListener('click', async (event) => {
          event.preventDefault();

          disableFilters();
          const optionsDiv = event.target.closest('.annotation-options');
          const tagsManagementControlsDiv = await createTagManagementControls(event, 'annotations');
          if(tagsManagementControlsDiv) {
            optionsDiv.insertAdjacentElement('beforebegin', tagsManagementControlsDiv);
            optionsDiv.classList.add('hide');
            // _calculatePlacementOfDiv(tagsManagementControlsDiv, optionsDiv);
          }

          const { dataset: { markId, userMarkRecordId } } = optionsDiv;
          updateTagManagementElements(markId, 'name', 'asc', userMarkRecordId);

          enableFilters();
        })
      }
    })
  }
}

/**
 * Change handler for text filter sensitivity element.
 */
function textFilterSensitivityChangeHandler() {
  const textSensitivityElem = document.querySelector("#case-sensitivity");
  textSensitivityElem.addEventListener('change', async (event) => {
    event.preventDefault();

    disableFilters();

    let options = setOptionValues();
    options.spinner = {
      name: 'text-filter',
      targetSelector: 'div #clear-all-filters',
      insertPosition: 'afterbegin',
    };

    if (options.filters.text.words.trim()) {
      await updateCollection(options);
      manageFilterAccordions();
    }

    enableFilters();
  })
}
/**
 * Click event handler for text filter element
 */
function textFilterGroupingClickHandler() {
  const textGroupingElem = document.querySelector("#word-grouping");
  if(textGroupingElem) {
    textGroupingElem.addEventListener('change', async (event) => {
      event.preventDefault();

      disableFilters();

      const options = setOptionValues();

      options.spinner = {
        name: 'text-filter',
        targetSelector: 'div #clear-all-filters',
        insertPosition: 'afterbegin',
      };

      if (options.filters.text.words.trim()) {
        await updateCollection(options);
        manageFilterAccordions();
      }

      enableFilters();
    })
  }
}
/**
 * Tag filter toggle buttons handler.
 */
function tagToggleButtonsClickHandler() {
  const toggleButtons = document.querySelectorAll('[data-toggle-icon]');

  // event handlers to switch the icon on the filter toggle elements, when clicked
  toggleButtons.forEach(toggleButton => {
    toggleButton.addEventListener('click', (event) => {
      event.preventDefault();

      togglerIconsHandler(toggleButton);
    });
  });
}

/**
 * Add text input handler for keyUp
 */
function addKeyUpTextInputOnTagsFilterHandler() {
  const tagsFilterInputText
    = document.querySelector('#filter-by-tag');
  if(tagsFilterInputText) {
    tagsFilterInputText.addEventListener('keyup', async (event) => {
      event.preventDefault();
      event.stopPropagation(); // ensure keyup for color selector (or any other reason) doesn't fire

      if (event.key === 'Enter') {
        disableFilters();

        const selectedTags = document.querySelectorAll('.selected-tags .tag');
        const availableTags = document.querySelectorAll('.existing-tags .tag');

        let tag = Array.from(availableTags).find((tag) => {
          if(tag.dataset.text === tagsFilterInputText.value) {
            return tag.dataset.tagId;
          }
        });

        if (tag) {
          // let { dataset: { tagId } } = tag;
          tagsFilterInputText.value = ''; // clear the input field
          console.log(`Add filter tag: ${tagsFilterInputText.value}`);
          // const tag = document.querySelector(`.tag[data-id="${tagId}"]`);
          tag.dispatchEvent(new Event('click'));
        } else {
          // already filtering by that tag
          tag = Array.from(selectedTags).find(tag => {
            if (tag.dataset.text === tagsFilterInputText.value) {
              return tag.dataset.id;
            }
          });

          if(tag) {
            console.log(`Already filtering by tag: ${tagsFilterInputText.value}`);
            // const tag = document.querySelector(`.tag[data-id="${tagId}"]`);
            tag.style.borderWidth = '1.25rem';
            setTimeout(() => {
              tag.removeAttribute('style');
            }, 2000);
          }
        }

        enableFilters();
      } else {
        updateTagFilterElements(event);
      }
    })
  }
}

/**
 * Add click handlers to filter tag elements in the filter-by-tag filter.
 */
function addHandlersToTagsInFilter() {
  const existingTags = document.querySelectorAll('.existing-tags .tag');
  const selectedTags = document.querySelectorAll('.selected-tags .tag');

  if (existingTags) {
    existingTags.forEach((tag) => {
      tag.dataset.action = 'add';
      if (!tag.dataset.listenerAttached) {
        tag.addEventListener('click', async (event) => {
          disableFilters();

          await filterTagClickHandler(event);

          enableFilters();
        })
      }

      tag.dataset.listenerAttached = 'true';
    });

    if (selectedTags) {
      selectedTags.forEach((tag) => {
        tag.dataset.action = 'remove';
        if (!tag.dataset.listenerAttached) {
          tag.addEventListener('click', async (event) => {
            disableFilters();

            await filterTagClickHandler(event);

            enableFilters();
          })
        }

        tag.dataset.listenerAttached = 'true';
      })
    }
  }

  const tagGroupingElem = document.querySelector('#tag-grouping');

  if(tagGroupingElem) {
    tagGroupingElem.addEventListener('change', async (event) => {
      event.preventDefault();

      disableFilters();

      const options = setOptionValues();

      options.spinner = {
        name: 'tags-filter',
        targetSelector: 'div #clear-all-filters',
        insertPosition: 'afterbegin',
      };

      if (options.filters.tags.ids.length > 0) {
        await updateCollection(options);
        manageFilterAccordions();
      }

      enableFilters();
    })
  }
}

/**
 * Emphasize tags on existing tags collection that are currently selected for filtering.
 */
function emphasizeFilteredTagsOnAnnotations() {
  const tagsFilteringBy = document.querySelectorAll('#filter-tags-expanded .selected-tags .tag');
  let highlightTagCollections= document.querySelectorAll('#annotations-collection .show-tags');

  const filteringByTextCollection = Array.from(tagsFilteringBy).map((tag) => {
    return tag.dataset.text;
  });

  // thicker border to emphasize the tag
  const borderWidth = '0.15rem';
  const emphasizingColorPercentage = '60';

  highlightTagCollections.forEach(collection => {
    collection.querySelectorAll('.tag').forEach((tagDiv) => {
      // emphasize the tag by changing its border width
      const { dataset: { text } } = tagDiv;
      const color = window.getComputedStyle(tagDiv).borderColor;
      if(filteringByTextCollection.includes(text)) {
          if(tagDiv.style.borderWidth !== borderWidth) {
            tagDiv.style.borderWidth = borderWidth;
            tagDiv.borderColor = darkenColor(color, emphasizingColorPercentage);
          }
        } else {
          // if the tag was emphasized, remove the emphasis
          if(tagDiv.style.borderWidth === borderWidth) {
            tagDiv.style.borderWidth = '1px';
            tagDiv.style.borderColor = lightenColor(color, emphasizingColorPercentage);
          }
        }
    });
  })
}

/**
 * Toggle the filter expand/collapse icon in the given toggler.
 *
 * @param toggleButton
 */
function togglerIconsHandler(toggleButton) {
  const isExpanded = toggleButton.getAttribute('aria-expanded') === 'true';

  // Update icon visibility
  const plusIcon = toggleButton.querySelector('.fa-plus');
  const minusIcon = toggleButton.querySelector('.fa-minus');

  if (!isExpanded) {
    plusIcon.style.display = 'none';
    minusIcon.style.display = 'inline-block';
  } else {
    plusIcon.style.display = 'inline-block';
    minusIcon.style.display = 'none';
  }
}

/**
 * Add handler for tags view element.
 */
function addMarkTagsFullViewHandler() {
  const annotationTagsWrapper = document.querySelectorAll('.assigned-tags-wrapper');
  if (annotationTagsWrapper) {
    annotationTagsWrapper.forEach((tagGutter) => {
      const viewIcon = tagGutter.querySelector('a');
      const closeButton = tagGutter.querySelector('.close-button');

      // tags collection div
      if(viewIcon) {
        viewIcon.addEventListener('click', (event) => {
          event.preventDefault();

          const tagDiv = tagGutter.querySelector('.annotation-options');
          if (tagDiv) {
            tagDiv.classList.remove('hide');
          }
        })
      }

      // close button
      if(closeButton) {
        closeButton.addEventListener('click', (event) => {
          event.preventDefault();

          const tagDiv = tagGutter.querySelector('.annotation-options');
          if (tagDiv) {
            tagDiv.classList.add('hide');
          }
        })
      }
    });
  }
}

/**
 * Handler for links and button on an annotation options div.
 *
 * @param event
 * @param action
 * @param annotation
 * @returns {Promise<void>}
 */
async function annotationActionHandler(event, action, annotation) {
  event.preventDefault();

  const {target} = event;
  let actionBtn;
  let name;

  if (action === 'delete') {
    let response = confirm('Are you sure you want to delete this annotation?');

    // if 'cancel'
    if (!response) {
      return;
    }
  }

  actionBtn = annotation.querySelector('.user-mark-edit');
  const options = setOptionValues();

  let data = actionBtn?.dataset;

  let color;
  if (annotation.nodeName === 'DIV') {
    color = annotation.querySelector('.user-highlight').style.backgroundColor;
  } else {
    color = annotation.querySelector('[headers="annotation"]').style.backgroundColor;
  }

  options.modifyCollection
    = {
    type: 'UserMark',
    marks: [{
      action,
      htmlContentId: data.hid,
      userMarkId: data.userMarkRecordId,
      color,
      markId: data.markId
    }]
  };

  let markId = event.target.closest('.annotation-options').dataset.markId;
  let targetSelector;
  let insertPosition;
  if (action === 'delete') {
    name = 'delete-annotation';
    targetSelector = `[data-mark-id="${markId}"] .highlight-colors#delete-highlight`;
    insertPosition = 'beforeEnd';
  } else {
    name = 'change-color';
    targetSelector = `[data-mark-id="${markId}"] .highlight-colors .${target.classList[0]}`;
    insertPosition = 'afterend'
  }

  options.spinner = {
    name,
    targetSelector,
    insertPosition
  };

  await updateCollection(options);
  manageFilterAccordions();
}

/**
 * Hook up action handlers for marks listed in annotations page
 */
async function addAnnotationsActionHandlers() {
  const annotations = document.querySelectorAll('[data-annotation]');

  // add handlers for all links in each annotation div
  if (annotations) {
    annotations.forEach((annotation) => {
      // skip if annotation is empty container (when there are no annotations in collection).
      if (annotation.dataset.annotation === 'empty') {
        return;
      }

      const {collection: {style}} = getCurrentOptions();
      const anchor = annotation.querySelector("a[data-mark-id]");
      const action = anchor.dataset.markAction;

      let highlightTextContainer;
      if (style === 'cards') {
        highlightTextContainer = annotation.querySelector('.user-highlight');
      } else if (style === 'list') {
        highlightTextContainer = annotation.querySelector('.user-highlight > [headers="annotation"]');
      }

      const optionsDiv = annotation.querySelector('.annotation-options');

      const colors = appColors();
      let colorAnchorElem, newColor, newColorValue;
      const copyElem = annotation.querySelector('[data-name="copy-text"]');

      // 1.  copy button handler
      if(copyElem) {
        try {
          copyElem.addEventListener('click', async (event) => {
            event.preventDefault();

            // TODO: Remove console output? Better way to display to the user if there was an error..
            const textWithoutPageNumbers = stripPageNumbers(highlightTextContainer.innerText);
            await copyToClipboard(textWithoutPageNumbers);

            // Change the copy text icon to green 'clicked' version
            const copyIcon = event.target; // annotation.querySelector('[data-name="copy-text"]';
            const {classList} = copyIcon;
            classList.remove('fa-copy');
            classList.add('fa-file-circle-check');

            // wait 2s before changing back the icon
            setTimeout(function () {
              console.log('waited 2 seconds.');
              classList.remove('fa-file-circle-check');
              classList.add('fa-copy');
            }, 2000);
          });
        } catch(error) {
          const msg = `Error copying text to clipboard: ${error.message}`;
          console.log(msg);
          const notice = new AsyncNotice(msg, 'error', true);
          notice.create({error});
          notice.remove(3);
        }
      }

      // 2. delete handler for options div trash can
      const optionsDivDeleteElem = annotation.querySelector('[data-name="remove-highlight"]');
      optionsDivDeleteElem.addEventListener(
        'click',
        async (e) => {
          await annotationActionHandler(e, 'delete', annotation)

          // hide the option div
          optionsDiv.classList.add('hide');
        });

      // 3. close icon handler
      const closeElem = annotation.querySelector(".close-button");
      if(closeElem) {
        closeElem.addEventListener('click', (event) => {
          event.preventDefault();

          optionsDiv.classList.add('hide');
        })
      }

      // 4. add handlers to change the highlight colors circles
      for (let [key, value] of Object.entries(colors)) {
        colorAnchorElem = annotation.querySelector(`[title="${key}"]`);

        // event listener for color option
        colorAnchorElem.addEventListener('click', async (event) => {
          event.preventDefault();
          const {target: {parentElement: targetParent}} = event;
          newColor = targetParent.title;
          newColorValue = colors[newColor];

          // store current color to revert if there's an error
          annotation.dataset.currentColor = highlightTextContainer.style.backgroundColor;

          // change the background color to the newly selected color
          highlightTextContainer.setAttribute('style', `background-color: ${value}`);
          console.log(`changing highlight color to ${newColor}`);

          try {
            // TODO: call to update color - async call

            await annotationActionHandler(event, 'edit', annotation);
          } catch (error) {
            console.log(`Issue handling this highlight.\n\n ${error.message} \n\n ${error.stack}`);
          }

          // hide the option div
          // optionsDiv.classList.add('hide');
        });
      }

      // 5. 'edit' pencil handler
      if (action === 'edit') {
        anchor.addEventListener('click', (event) => {
          event.preventDefault();

          const selector = `.annotation-options[data-user-mark-record-id="${anchor.dataset.userMarkRecordId}"][data-mark-id="${anchor.dataset.markId}"]`;
          const optionsDiv
            = document.querySelector(selector)
          if(optionsDiv) {
            optionsDiv.classList.remove('hide');
          }
        });
      }
    })
  }
}

/**
 * Adds event listener to the clear filter buttons
 *
 * @returns {Promise<void>} - A Promise that resolves when the event listener is added
 */
async function addClearFiltersHandlers() {
  const clearAllBtn = document.querySelector('#clear-all-filters');
  if (clearAllBtn) {
    clearAllBtn.addEventListener('click', async (event) => {
      await clearFiltersHandler(event, 'all');
    });
  }

  const clearColorsFilterBtn = document.querySelector('#clear-colors-filter');
  if(clearColorsFilterBtn) {
    clearColorsFilterBtn.addEventListener('click', async (event) => {
      await clearFiltersHandler(event, 'colors');
    })
  }

  const clearTagsFilterBtn = document.querySelector('#clear-tags-filter');
  if(clearTagsFilterBtn) {
    clearTagsFilterBtn.addEventListener('click', async(event) => {
      await clearFiltersHandler(event, 'tags');
    });
  }

  const clearTextFilterBtn = document.querySelector('#clear-text-filter');
  if(clearTextFilterBtn) {
    clearTextFilterBtn.addEventListener('click', async (event) => {
      await clearFiltersHandler(event, 'text');
    })
  }
}

/**
 * Clear all the filters and update the collection.
 *
 * @param {Event} event - The event triggered by clicking the 'Clear All' button.
 * @param filterType
 * @returns {Promise<void>} - A promise that indicates the completion or failure of the operation.
 */
async function clearFiltersHandler(event, filterType = 'all') {
  event.preventDefault();

  disableFilters();
  let options = getCurrentOptions();
  let targetSelector = `#clear-${filterType}-filter`;
  if(filterType === 'all') {
    targetSelector =  '#clear-all-filters';
    options = setOptionValues(true);
  }

  options.spinner = {
    name: `clear-${filterType}-filter`,
    targetSelector,
    insertPosition: 'afterend'
  }

  options = clearFilterControls(options, filterType);

  const collection = document.querySelector('[data-paginated-collection]');
  collection.dataset.options = JSON.stringify(options);

  await updateCollection(options);
  emphasizeFilteredTagsOnAnnotations();
  manageFilterAccordions();
  enableFilters();
}

/**
 * Handle the clearing of a filter in the filters drop down.
 * The default is to clear all the filters. You can filter just a specific filter
 * by setting filter type to one of: 'colors','tags','text'.
 *
 * @param options
 * @param filterType
 */
function clearFilterControls(options, filterType = 'all') {
  /* colors filter */
  if(filterType === 'all' || filterType === 'colors') {
    /* reset color controls */
    let colorFilters
        = document.querySelectorAll('#annotation-filters input[type="checkbox"]')
    colorFilters.forEach((checkbox) => {
      checkbox.checked = false;
    });

    options.filters.colors = [];
  }

  /* text filter */
  if(filterType === 'all' || filterType === 'text') {
    // reset text field
    let textFilter = document.querySelector('#filter-by-text');
    if (textFilter) {
      textFilter.value = '';
    }

    options.filters.text.words = '';

    // reset case sensitivity (insensitive)
    let caseSensitivity = document.querySelector('#case-sensitivity');
    if(caseSensitivity) {
      caseSensitivity.checked = false;
    }

    options.filters.text.sensitivity = 'insensitive';

    // reset text grouping ('all')
    let textGrouping = document.querySelector('#word-grouping');
    if(textGrouping) {
      textGrouping.checked = true;
    }

    options.filters.text.grouping = 'all';
  }

  /* tags filter */
  if(filterType === 'all' || filterType === 'tags') {
    // clear text in tags filter input
    const filterByTagInput = document.querySelector('#filter-by-tag');
    if(filterByTagInput) {
      filterByTagInput.textContent = '';
    }

    options.filters.tags.ids = [];

    // reset tag grouping ('any')
    let tagGrouping = document.querySelector('#tag-grouping');
    if(tagGrouping) {
      tagGrouping.checked = false;
    }

    options.filters.tags.grouping = 'any'

    // move filter tag elements back to user tags div
    const selectedTagsCollection = document.querySelectorAll('.selected-tags .tag');
    if(selectedTagsCollection) {
      selectedTagsCollection.forEach(element => {
        if (element.nodeName === 'DIV') {
          moveTagFilterTagElement(element);
        }

        if (element.nodeName === '#text') {
          element.remove();
        }
      })
    }
  }

  manageFilterAccordions();

  return options;
}

/**
 * Returns true if the page is currently filtered by the given filter type.
 *
 * @param options * @param filterType
 * @param filterType
 * @returns {boolean}
 */
function isTypeFiltered(options, filterType) {
  let response = false;
  switch(filterType) {
    case 'colors':
      response = options.filters[filterType].length > 0;
      break;
    case 'tags':
      response = options.filters[filterType].ids.length > 0;
      break;
    case 'text':
      response = options.filters[filterType].words.length > 0;
      break;
  }

  return response;
}

/**
 * Manages all the DOM elements related to a filter based on the options object.
 * Update each filter accordion component such as the border color, icons, toggles the accordion, and
 * handles the visibility of the clear filter button.
 */
function manageFilterAccordions() {
  const options = setOptionValues();

  // for each filter type
  ['colors', 'tags', 'text'].forEach(filterType => {
    let filterIndicatorIcon = document.querySelector(`#${filterType}-filter-indicator`);
    let filterIsEnabled = isTypeFiltered(options, filterType);

    switch(filterType) {
      case 'colors':
        updateColorFilterAccordionHeader(options, filterIsEnabled, filterIndicatorIcon);
        break;
      case 'tags':
        updateTagsFilterAccordionHeader(options, filterIsEnabled, filterIndicatorIcon);
        break;
      case 'text':
        updateTextFilterAccordionHeader(options, filterIsEnabled, filterIndicatorIcon);
        break;
    }

    // manage the visibility of the filter icon on the accordion
    if(filterIndicatorIcon) {
      if (filterIsEnabled) {
        filterIndicatorIcon.classList.remove('hide');
      } else {
        filterIndicatorIcon.classList.add('hide');
      }
    }

    // handle the filtered class on the accordion
    let filterSection = filterIndicatorIcon.closest('.filter-section');
    if (filterSection) {
      if(filterIsEnabled) {
        filterSection.classList.add('filtered');
      } else {
        filterSection.classList.remove('filtered');
      }
    }

    // accordion toggle icon
    const toggleAnchor = document.querySelector(`#filter-${filterType}-toggle`);
    if (toggleAnchor) {
      const minusIcon = toggleAnchor.querySelector('.fa-minus');
      const plusIcon = toggleAnchor.querySelector('.fa-plus');

      if(filterIsEnabled) {
        if (minusIcon) {
          minusIcon.style.display = 'block';
        }

        if(plusIcon) {
          plusIcon.style.display = 'none';
        }
      } else {
        if(minusIcon) {
          minusIcon.style.display = 'none';
        }
        if (plusIcon) {
          plusIcon.style.display = 'block';
        }
      }
    }


    // handle accordion state (open/closed) based on icon (+ or -) that is showing
    const filterAccordion = document.querySelector(`#filter-${filterType}-expanded`);
    const enabledAccordionIcon = toggleAnchor.querySelector('i[style="display: block;"]');
   if(enabledAccordionIcon.classList.contains('fa-plus') && filterAccordion.style.display !== 'none')  {
      jQuery(`#filter-${filterType}-expanded`).foundation('toggle');
    }

    // the clear filter button
    const clearFilterButton = document.querySelector(`#clear-${filterType}-filter`);
    if(clearFilterButton) {
      if(filterIsEnabled) {
        clearFilterButton.classList.remove('hide');
      } else {
        clearFilterButton.classList.add('hide');
      }
    }
  });

  // handle clear all visibility
  const clearAllBtn = document.querySelector('#clear-all-filters');

  // manage clear all button availability
  if (isCollectionFiltered()) {
    clearAllBtn.classList.remove('disabled');
    clearAllBtn.removeAttribute('disabled');
    clearAllBtn.removeAttribute('aria-disabled');
  } else {
    clearAllBtn.classList.add('disabled');
    clearAllBtn.setAttribute('disabled', '');
    clearAllBtn.setAttribute('aria-disabled', '');
  }
}

/**
 * create a tag filter element. if action is 'add', the event handler will handle adding the tag
 * to the filter list from the 'Your Tags' collection. If action is remove, the handler will handle
 * removing the tag from the filter list and adding back to the 'Your Tags' collection.
 * @param tagData
 * @param action // ['add' , 'remove']
 * @returns {HTMLDivElement}
 */
function createFilterTagElement(tagData, action = 'add') {
  const { color, id, lastUpdated, text, usage } = tagData;

  // create a tag element
  const tag = document.createElement('div');
  tag.classList.add('tag', `${color}`);

  tag.dataset.tagId = id;
  tag.dataset.color = color;
  tag.dataset.lastUpdated = lastUpdated;
  tag.dataset.text = text;
  tag.dataset.usage = usage;
  tag.dataset.action = action;

  const span = document.createElement('span');
  span.innerHTML = text;
  tag.appendChild(span);

  tag.addEventListener('click', async (event) => {
    await filterTagClickHandler(event);
  })

  return tag;
}

/**
 * Handler to add or remove a tag from the tags filter list.
 *
 * @param event
 */
async function filterTagClickHandler(event) {
  event.preventDefault();

  let { target: tag } = event;
  if(tag.nodeName === 'SPAN') {
    tag = tag.closest('.tag');
  }
  moveTagFilterTagElement(tag);

  const options = setOptionValues();
  options.spinner = {
    name: 'tag-filter',
    targetSelector: 'div #clear-all-filters',
    insertPosition: 'afterbegin',
    withWrapper: false
  };

  await updateCollection(options);

  emphasizeFilteredTagsOnAnnotations();
  manageFilterAccordions();
}

/**
 * Move a tag filter element between existing and suggested areas.
 *
 * @param tag
 */
function moveTagFilterTagElement(tag) {
  const { dataset: { action } } = tag;

  let selectedTagsCollection = document.querySelector('.selected-tags');
  let userTagsCollection = document.querySelector('.existing-tags');
  if (action === 'add') {
    selectedTagsCollection = removeNonCharacterNodes(selectedTagsCollection);

    if (selectedTagsCollection.childNodes.length === 0) {
      selectedTagsCollection.appendChild(tag);
    } else {
      selectedTagsCollection.insertAdjacentElement('afterbegin', tag);
    }

    // swap action
    tag.dataset.action = 'remove';
  }

  if (action === 'remove') {
    userTagsCollection = removeNonCharacterNodes(userTagsCollection);
    if (userTagsCollection.childNodes.length === 0) {
      userTagsCollection.appendChild(tag);
    } else {
      userTagsCollection.insertAdjacentElement('afterbegin', tag);
    }

    // swap action
    tag.dataset.action = 'add';
  }
}

/**
 * Return the case sensitivity selection from text sensitivity filter radio buttons.
 *
 * @param selectorGroup
 * @returns {string}
 */
function getTextSensitivity(selectorGroup) {
  let selection = 'sensitive';
  selectorGroup.forEach((radio) => {
    if (radio.checked) {
      selection = radio.value;
    }
  })

  return selection;
}

/**
 * Update the collection of Annotations based on paging / sorting / filtering selected.
 * calls addActionHandlers after the collection is updated
 *
 * @param options
 * @returns {Promise<{success: boolean}>}
 */
async function updateCollection(options) {
  const { collection: { paginatorName, paginationControlsSelector } } = options;
  const { spinner: spinnerOptions } = options;
  // spread assignment used to ensure correct order of params in Spinner construction
  const { name, targetSelector, insertPosition = 'afterend', withWrapper = false } = spinnerOptions;
  const  spinner = new Spinner(name, targetSelector, insertPosition, withWrapper);

  try {
    let collectionElement = document.querySelector('[data-paginated-collection]');
    let paginationControls = document.querySelector(`${paginationControlsSelector}`)
    let sortElement = document.querySelector("#sort-by");

    if (sortElement) {
      options.sort = sortElement.value;
    }

    spinner.add();

    let data = {paginatorName, options};
    let url = collectionElement.dataset.refreshUrl;
    let result = await new FetchHelpers().fetchData('POST', url, data);
    let {success, messages} = result;

    if (success) {
      // set the page param to '1' on url
      // const params = new URLSearchParams(location.search);
      // params.delete('page');
      // params.append('page', '1');
      // window.history.replaceState({}, '', `${location.pathname}?${params}`);
      window.history.replaceState({}, '', `${location.pathname}`);

      /* update collection element with new rows, related attributes and event handlers */
      const newCollectionHolder= document.createElement('div');
      newCollectionHolder.innerHTML = result['collection'];
      collectionElement.replaceWith(newCollectionHolder.firstElementChild);

      // update collection wrapper
      collectionElement.dataset.collection = JSON.stringify(options);

      // update options data attribute
      collectionElement.dataset.options = result['options'];

      /* add event handlers for elements in the new collection */
      await addAnnotationsActionHandlers();
      addMarkTagsFullViewHandler();

      // update totals
      let totalsElem= document.querySelector('.annotation-items');
      const temp = document.createElement('div');
      temp.innerHTML = result['total'];
      totalsElem.replaceWith(temp.firstChild);

      // update navigation controls
      let nav = result['nav'].toString();
      let newNav = document.createRange().createContextualFragment(nav);
      paginationControls.replaceWith(newNav);

      await addPaginationNavigationHandlers(paginatorName);

      // display any messages available
      if (messages) {
        let message = messages.join("\n");
        let notice = new AsyncNotice(message, 'warning', true, 5);
        await notice.create({}, true);
        notice.remove();
      }

      // re-init foundation
      jQuery(document).foundation();
    } else {
      let message = 'There was an error updating the collection.';
      let notice = new AsyncNotice(message, 'error');
      let stack = result['error'].stack.toString();
      await notice.create({message, stack}, true);
      notice.remove();

      let regex = /(\[.*])/;
      let target = targetSelector.match(regex);
      let element = document.querySelector(`${target[1]}`).closest('[data-annotation]');
      element.querySelector('.user-highlight').style.backgroundColor = element.dataset.currentColor;

      //TODO: report error via js async update of notices
      console.log(message);
    }
    spinner.remove();
  } catch (error) {
    console.log(`${error}`);
    let message = 'There was an error updating the annotations list. Please refresh the page.';
    let notice = new AsyncNotice(message, 'error');
    let stack = error.stack.toString();
    await notice.create({message, error, stack}, true);
    notice.remove(5);

    let regex = /(\[.*])/;
    let target = targetSelector.match(regex);
    if(target) {
      let element = document.querySelector(`${target[1]}`)?.closest('[data-annotation]');
      if (element) {
        element.querySelector('.user-highlight').style.backgroundColor = element.dataset.currentColor;
      }
    }

    spinner.remove();
    return { success: false };
  }

  spinner.remove();
  return { success: true };
}

/**
 * Get the options from the collection element's dataset in DOM.
 * @returns {*}
 *
 */
function getCurrentOptions() {
  const collectionElement = document.querySelector('[data-paginated-collection]');
  const currentOptions = collectionElement?.dataset?.options ?? {};
  let options = {};

  try {
    if (currentOptions) {

      const json = collectionElement?.dataset?.options ?? '';
      let data = JSON.parse(json);
      options = structuredClone(data);

      if (options.filters) {
        options.filters = {};
        options.filters.colors = data.filters['colors'] ?? [];
        options.filters.text = data.filters.text ?? { grouping: 'all', sensitivity: 'insensitive', words: '' };
        options.filters.tags = data.filters.tags ?? { grouping: 'all', ids: [] };
      }
    } else {
      options = setOptionValues();
    }

  } catch (error) {
    // TODO: add log error msg...
    console.log(error.message);
    options = {};
  }

  return options;
}

/**
 * Returns the default annotations filters.
 *
 * @return {Object} - Default filters object with two properties: text and colors.
 *                   The text property contains an object with three properties: words, sensitivity, and grouping.
 *                   - The words property is an array of strings.
 *                   - The sensitivity property is a string that specifies the case sensitivity for text matching.
 *                   - The grouping property is a string that specifies the grouping behavior for text matching.
 *                   The colors property is an array of color values.
 */
function defaultFilters() {
  return {
    text: {
      sensitivity: 'insensitive',
      grouping: 'all',
      words: ''
    },
    colors: [],
    tags: {
      grouping: 'all',
      ids: []
    }
  }
}

/**
 * Returns the marks array from sessionStorage
 * @returns {any}
 * @private
 */
function getSectionMarksFromSessionStorage() {
  let sessionMarks = sessionStorage.getItem('marks');
  if (!sessionMarks) {
    sessionMarks = clearMarkObjectInSession();
  }
  return JSON.parse(sessionMarks);
}

/**
 * Clear the mark object in session storage
 * @returns {string}
 * @private
 */
function clearMarkObjectInSession() {
  let marks = JSON.stringify([]);
  sessionStorage.setItem('marks', marks);

  return marks;
}

/**
 * Update the number of marks, on the highlights menu.
 */
function updateStateOfHighlightsMenuDeleteButtons(marksCount) {
  const deleteAll = document.querySelector('#remove-all-annotations');
  const deleteAllText = deleteAll.querySelector('#count');
  const deleteLast = document.querySelector('#remove-last-annotation')

  let text = null;

  if (marksCount > 0) {
    text = `(${marksCount})`;

    // update visibility of buttons
    deleteAll.removeAttribute('disabled');
    deleteLast.removeAttribute('disabled');
  } else {
    // update visibility of buttons
    deleteAll.setAttribute('disabled', 'disabled');
    deleteLast.setAttribute('disabled', 'disabled');
  }

  // update button text
  deleteAllText.innerText = text;
}

/**
 * Generate the tags management div with updated suggested tags.
 *
 * @param event
 * @param sortBy - one of: ('name', 'time', 'usage'); default: 'name'
 * @param direction - one of: ('asc', 'desc'); default: 'asc'
 */
function updateTagFilterElements(event, sortBy = 'name', direction = 'asc') {
  const textInputElem = document.querySelector('#filter-by-tag');
  const inputText = textInputElem.value;
  const existingTagsWrapper = document.querySelector('.existing-tags');
  // const existingTags = existingTagsWrapper.querySelectorAll('.tag');
  const allTagsData = JSON.parse(sessionStorage.getItem('userTags'));

  const selectedTagElems = document.querySelectorAll('.selected-tags .tag');
  let selectedTags = Array.from(selectedTagElems).map((tag) => {
    let { dataset: { color, id, lastUpdated, text, usage } } = tag;
    return { color, id, lastUpdated, text, usage };
  })

  /* manage tags management variables */
  const sortFunction = tagsSortingFunction(sortBy, direction);

  // filter() function to remove tags already added to mark
  const filterAlreadyAddedTags = (tag) => !selectedTags.some((selectedTag) => selectedTag.text === tag.text);

  let filteredTags
    = allTagsData
    .filter(filterAlreadyAddedTags)
    .sort(sortFunction);

  // grab previous text input value, save current
  const previousInputTextCount = sessionStorage.getItem('current-mark-new-tag-text-count');
  sessionStorage.setItem('current-mark-new-tag-text-count', inputText.length);

  // For performance reasons, we keep and use a filtered tags list instead of always rechecking the full
  // tags list. Decide when we need to reset the filtered tags list.
  let resetTagsList
    = ((filteredTags.length ?? 0) === 0) || // if the filtered Tag List is empty
    (textInputElem.value.length === 0) || // if there are no characters in the new tag input text field
    (previousInputTextCount > textInputElem.value.length); // if the user removed a character

  if (resetTagsList) {
    // copy by value, not reference
    filteredTags = [...allTagsData].sort(sortFunction);
  }

  // list of all visible tag names:
  // (1) from filtered tags list
  // (2) existing tags
  // (3) newly added tags that have not been saved.
  const allTagElems = filteredTags.concat(selectedTags);
  const visibleTagNamesMap = new Map();
  allTagElems.forEach((item) => {
    visibleTagNamesMap.set(item.text, {id: item.id, color: item.color});
  });

  // manage suggested tags
  let tagDiv;

  // suggestions div
  let newExistingTagsWrapper = existingTagsWrapper.cloneNode();

  let tagTexts = arrayColumn(allTagsData, 'text');
  if ((filteredTags?.length ?? 0) > 0) {
    // clear the filtered tags list and fill with remaining possible matches, below
    for (let tagData of filteredTags) {
      let {text} = tagData;

      if (text.startsWith(textInputElem.value) && tagTexts.includes(text)) {
        tagDiv = createFilterTagElement(tagData);
        tagDiv.setAttribute('title', `Usage: ${tagData.usage ?? 0}`);
        tagDiv.setAttribute('data-id', tagData.id);
        newExistingTagsWrapper.appendChild(tagDiv);

        const deleteTagElem = tagDiv.querySelector('[data-tag-type="delete"]');
        if(deleteTagElem) {
          deleteTagElem.remove();
          tagDiv.appendChild(newExistingTagsWrapper);
        }
      }
    }
  }

  if (newExistingTagsWrapper.childNodes.length > 0) {
    while (existingTagsWrapper.firstChild) {
      existingTagsWrapper.removeChild(existingTagsWrapper.firstChild);
    }
    existingTagsWrapper.replaceWith(newExistingTagsWrapper);
  } else {
    removeAllChildNodes(existingTagsWrapper);

    const noneSpan = document.createElement('span');
    noneSpan.innerText = 'No suggestions.';
    existingTagsWrapper.appendChild(noneSpan);
  }
}

/**
 * Utility method to remove empty nodes ('', '\n', or '\t') from a NodeList
 * @param collectionWrapper
 */
function removeNonCharacterNodes(collectionWrapper) {
  collectionWrapper.childNodes.forEach(node => {
    if(node.nodeType === Node.TEXT_NODE && node.textContent.trim() === '') {
      node.parentNode.removeChild(node);
    }
  })

  return collectionWrapper;
}

/**
 * fetch user tags data from DB if it has not been already loaded into session storage
 */
async function fetchUserTags() {
  if(!sessionStorage.getItem('userTags')) {
    let fetchSuccess = false;
    const collectionElement = document.querySelector(' [data-collection]');
    let url = '';

    if(collectionElement) {
      url = collectionElement.dataset.refreshUrl;

      let data = { action: 'load' };
      let result = await new FetchHelpers().fetchData('POST', url, data);
      let {success, tags} = result;
      if(success) {
        sessionStorage.setItem('userTags', JSON.stringify(tags));
      }
    }

    if(!fetchSuccess) {
      const msg = "Could not load your tag collection.";
      const notice = new AsyncNotice(msg, 'warning');
      await notice.create({}, true);
      notice.remove();
    }
  }
}

/**
 * Return the colors we are filtering by, with the color filter.
 *
 * @param options
 * @returns {*}
 */
function colorFilterSelections(options) {
  return options.filters.colors;
}

/** return the tag names of all tags we are filtering by, with the tags filter.
 *
 * @param options
 */
function tagsFilterNames(options) {
  const tagIds = options.filters.tags.ids;
  const tags = JSON.parse(sessionStorage.getItem('userTags'));
  if(tags.length === 0) {
    return [];
  }

  let tagNames = [];
  tagIds.forEach((tagId) => {
    let tagData = tags.find((tag) => {
      return tag.id === tagId;
    });

    if (tagData) {
      tagNames.push(tagData.text);
    }
  });

  return tagNames;
}

/** return the words we are filtering by, with the text filter.
 *
 * @param options
 */
function textFilterWords(options) {
  return options.filters.text.words.split(',');
}

/**
 * Manage the color filter accordion header details.
 * If the filter is enabled, add details of how many colors we are filtering by, to the title.
 *
 * @param options
 * @param filterIsEnabled
 * @param filterIndicatorIcon
 */
function updateColorFilterAccordionHeader(options, filterIsEnabled, filterIndicatorIcon) {
  // update the accordion header when filtering
  const filterTitleSpan = filterIndicatorIcon.nextElementSibling;
  filterTitleSpan.innerHTML = "&nbsp;Filter by color";

  if(filterIsEnabled) {
    // add number of colors to title
    const filterColors = colorFilterSelections(options);
    if(filterColors.length > 0) {
      const color = filterColors.length === 1 ? 'color' : 'colors';
      const title = filterTitleSpan.childNodes[0];
      filterTitleSpan.innerHTML = `${title.textContent}: ${filterColors.length} ${color}`;
    }
  }
}


/**
 * Manage the tags filter accordion header details.
 * If the filter is enabled, add details of how many tags we are filtering by, to the title.
 *
 * @param options
 * @param filterIsEnabled
 * @param filterIndicatorIcon
 */
function updateTagsFilterAccordionHeader(options, filterIsEnabled, filterIndicatorIcon) {
  // update the accordion header when filtering
  const filterTitleSpan = filterIndicatorIcon.nextElementSibling;
  filterTitleSpan.innerHTML = "&nbsp;Filter by tag";

  if(filterIsEnabled) {
    // add number of tags to title
    const filteredTagNames= tagsFilterNames(options);
    if(filteredTagNames.length > 0) {
      const tag = filteredTagNames.length === 1 ? 'tag' : 'tags';
      const title = filterTitleSpan.childNodes[0];
      filterTitleSpan.innerHTML = `${title.textContent}: ${filteredTagNames.length} ${tag} (${options.filters.tags.grouping})`;
    }
  }
}

/**
 * Manage the text filter accordion header details.
 * If the filter is enabled, add details of how many words we are filtering by, to the title.
 *
 * @param options
 * @param filterIsEnabled
 * @param filterIndicatorIcon
 */
function updateTextFilterAccordionHeader(options, filterIsEnabled, filterIndicatorIcon) {
  // update the accordion header when filtering
  const filterTitleSpan = filterIndicatorIcon.nextElementSibling;
  filterTitleSpan.innerHTML = "&nbsp;Filter by text";

  if(filterIsEnabled) {
    // add number of words to title
    const filterWords = textFilterWords(options);
    if(filterWords.length > 0) {
      const word = filterWords.length === 1 ? 'word' : 'words';
      const title = filterTitleSpan.childNodes[0];
      filterTitleSpan.innerHTML = `${title.textContent}: ${filterWords.length} ${word} (${options.filters.text.grouping})`;
    }
  }
}

/**
 * Returns true if the collection is being filtered by any of the filters.
 *
 * @returns {boolean}
 */
function isCollectionFiltered() {
  const { filters: { colors, tags: { ids: tagIds }, text: { words } } } = getCurrentOptions();
  return colors.length > 0 || tagIds.length > 0 || words.length > 0;
}

/**
 * Disable the filters with an overlay. This prevents multiple clicks by the users while a process hasn't finished.
 */
function disableFilters() {
  const filtersContainer = document.querySelector('#annotation-filters');

  insertOverlayOnElement(filtersContainer, 'filters-overlay');
}

/**
 * Enable the filters by removing the overlay, if when present.
 */
function enableFilters() {
  removeOverlayOnElement('filters-overlay')
}

export {
  addAnnotationPageEventHandlers, addAnnotationsActionHandlers, appColors, emphasizeFilteredTagsOnAnnotations,
  getSectionMarksFromSessionStorage, clearMarkObjectInSession, fetchUserTags, manageFilterAccordions, setOptionValues,
  updateStateOfHighlightsMenuDeleteButtons
};
